/**
 * Match Domain
 * The match the join made (auth.ts handleJoined opens it): the one messages
 * go to (services/nakama/match-send.ts), and its label, from which the world
 * it plays is read — by the lobby's board and by the session's end.
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { Presence } from '@heroiclabs/nakama-js';

// ==================== TYPES ====================

/** A Nakama match, as a join hands it back. */
export interface NakamaMatch {
  match_id: string;
  authoritative: boolean;
  label?: string;
  size: number;
  presences: Presence[];
  self: Presence;
}

// ==================== STORE ====================

interface MatchState {
  /** The match messages go to, or null with none joined. */
  matchId: string | null;
  /** Its label, the JSON the server gives every match (MatchLabel), or null. */
  label: string | null;
  setMatch: (match: Pick<NakamaMatch, 'match_id' | 'label'> | null) => void;
  reset: () => void;
}

const initialState = {
  matchId: null,
  label: null,
};

/**
 * Match Store
 * Holds the match the player is in: its id and its label (coordination info, not game state)
 */
export const useMatchStore = create<MatchState>()(
  devtools(
    (set) => ({
      ...initialState,

      /**
       * Set the match the join made, or none
       */
      setMatch: (match) => {
        console.log('[MatchStore] Setting match:', match?.match_id ?? null);
        set({ matchId: match?.match_id ?? null, label: match?.label ?? null }, false, 'setMatch');
      },

      /**
       * Reset the match store to initial state
       */
      reset: () => {
        console.log('[MatchStore] Resetting match store');
        set(initialState, false, 'reset');
      },
    }),
    {
      name: 'match-store',
    }
  )
);

// ==================== THE WORLD ====================

/**
 * What the client reads of a match's label — the JSON the server gives every
 * match (match-handler.go matchLabel: game_name, world_name, session_id, …),
 * which the join hands back with the match. client_parity_test.go holds each
 * field to a key the server writes.
 */
interface MatchLabel {
  world_name?: unknown;
}

/** The world a match plays, from its label. Empty when the label names none. */
export function worldOfLabel(label: string | undefined): string {
  if (!label) return '';
  try {
    const parsed: unknown = JSON.parse(label);
    const world = parsed && typeof parsed === 'object' ? (parsed as MatchLabel).world_name : undefined;
    return typeof world === 'string' ? world : '';
  } catch {
    return '';
  }
}

/**
 * The world the joined match plays, from its label — the server's word on it:
 * what the session's end names when the player leaves it before the server
 * has (authService.leaveEndedSession). Empty with no match joined.
 */
export function getMatchWorld(): string {
  return worldOfLabel(useMatchStore.getState().label ?? undefined);
}

/** Reactive {@link getMatchWorld}: what a lobby's board is titled. */
export function useMatchWorld(): string {
  return useMatchStore((s) => worldOfLabel(s.label ?? undefined));
}

// ==================== HANDLERS ====================

/**
 * The join made the match (auth.ts handleJoined): messages go to it from now
 * on (services/nakama/match-send.ts), and its label names its world.
 */
export function openMatch(match: Pick<NakamaMatch, 'match_id' | 'label'>): void {
  useMatchStore.getState().setMatch(match);
}
